import { fetchGetUnreviewedReportById, fetchGetUnreviewedReports } from '@/entities/inbox';
import { checkIcorDevice } from '@/entities/device';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { STUDY_STATUS } from '@/entities/study';
import { emitter, showMessageError } from '@/shared/lib';
import { useEmitter, useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import type { InstantReportUpdatedSocketMessage } from '@/shared/lib';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatInstantReport, formatInstantReports } from '../../lib/instantReportTab';
import type {
  InstantReportProps,
  InstantReportTabState,
  Pagination,
} from './instantReportTab.types';

/**
 * Drives the instant report list of a study's instant report tab.
 * Fetches the study's unreviewed reports when the tab is active for a started non-C1c study,
 * merges INSTANT_REPORT_UPDATED socket updates, gates the create modal on facility arrears, and
 * navigates to a report in the inbox.
 *
 * @param props - Tab props carrying isActive, the study data, isLoadedData and isC1cProviderStudy.
 * @returns The tab state, the aborted, archived and iCor flags, and the report and modal handlers.
 */
export const useInstantReportTab = ({
  isActive,
  data,
  isLoadedData,
  isC1cProviderStudy = false,
}: Pick<InstantReportProps, 'isActive' | 'data' | 'isLoadedData' | 'isC1cProviderStudy'>) => {
  const navigate = useNavigate();

  const [state, setState] = useSetState<InstantReportTabState>({
    isLoadedData: false,
    hasNoReport: false,
    hasInstantReport: false,
    isAbledToHasInstantReport: false,
    reports: [],
    page: 1,
    pageSize: 10,
    hideElementInitial: true,
    isOpenCreateInstantReportModal: false,
  });

  /**
   * Whether the study is in the aborted state. Memoized comparison of the study's status against STUDY_STATUS.ABORTED, recomputed only when that status changes.
   *
   * @returns True when the study status equals STUDY_STATUS.ABORTED.
   */
  const isAbortedStudy = useMemo(() => data?.status === STUDY_STATUS.ABORTED, [data?.status]);
  /**
   * Whether the study has been archived. Memoized read of the isArchived flag on the study data, recomputed whenever the study data object changes.
   *
   * @returns The study's isArchived flag, or undefined when no study data is available.
   */
  const isArchivedStudy = useMemo(() => data?.isArchived, [data]);

  /**
   * Loads a study's unreviewed instant reports for the tab. Fetches them by study id and stores the formatted rows, or on failure logs, surfaces an error message and stores an empty list, always marking the data loaded.
   *
   * @returns A promise resolving once the formatted reports have been stored in state.
   */
  const fetchReports = async () => {
    try {
      setState({ isLoadedData: false });
      const instantReports = await fetchGetUnreviewedReports({
        studyId: data.id,
      });
      const newReports = formatInstantReports(_.compact(instantReports));
      setState({
        isLoadedData: true,
        reports: newReports,
      });
    } catch (error) {
      console.error('Failed to fetch instant reports: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({
        reports: [],
        isLoadedData: true,
      });
    }
  };

  /**
   * Inserts an instant report announced over the socket but not yet present in the list. Fetches the unreviewed report by id, formats it and unshifts it onto a cloned report list while toggling the loaded flag, logging a failure and restoring the flag on error.
   *
   * @param reportId - Id of the instant report to load.
   * @returns A promise resolving once the report list state has been updated.
   */
  const handleAddNewInstantReportViaSocket = async (reportId: string) => {
    try {
      setState({ isLoadedData: false });
      const newInstantReport = await fetchGetUnreviewedReportById({ reportId });
      if (!newInstantReport) {
        setState({ isLoadedData: true });
        return;
      }
      const formattedInstantReport = formatInstantReport(newInstantReport);
      const clonedReports = _.cloneDeep(state.reports);
      clonedReports.unshift(formattedInstantReport);
      setState({
        isLoadedData: true,
        reports: clonedReports,
      });
    } catch (error) {
      console.error('Failed to fetch instant report: ', error);
      setState({ isLoadedData: true });
    }
  };

  /**
   * Opens the create-instant-report modal unless the facility is in arrears. For arrears 30 or 40 it emits the escalation popup with a callback that opens the modal afterwards, for 50 or 60 it only emits the popup, and otherwise it opens the modal directly.
   *
   * @returns Nothing.
   */
  const handleOpenCreateInstantReportModal = () => {
    const { arrears } = data.facility;
    if (_.includes(['30', '40'], arrears?.toString())) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, arrears, () => {
        setState({ isOpenCreateInstantReportModal: true });
      });
    } else if (_.includes(['50', '60'], arrears?.toString())) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, arrears);
    } else {
      setState({ isOpenCreateInstantReportModal: true });
    }
  };

  /**
   * Opens an instant report listed on the tab. Navigates to the inbox route for the clicked report's id.
   *
   * @param clickedReport - Instant report row that was clicked.
   * @returns Nothing.
   */
  const onClickReport = (clickedReport: any) => {
    navigate(`/studies/inbox/${clickedReport.id}`);
  };

  /**
   * Dismisses the create-instant-report modal. Clears its open flag in state.
   *
   * @returns Nothing.
   */
  const handleCancelCreateInstantReportModal = () => {
    setState({ isOpenCreateInstantReportModal: false });
  };

  /**
   * Refreshes the instant-report tab once a report has been requested. Returns to the first page, closes the create modal and reloads the report list.
   *
   * @returns Nothing.
   */
  const handleAfterCreatingInstantReport = () => {
    setState({
      page: 1,
      isOpenCreateInstantReportModal: false,
    });
    fetchReports();
  };

  /**
   * Keeps the instant-report table's paging in state. Compares the incoming current page and page size against the stored values and writes back only those that changed.
   *
   * @param pagination - Table pagination carrying the current page and page size.
   * @returns Nothing.
   */
  const handleTableChange = (pagination: Pagination) => {
    const { current, pageSize } = pagination;
    const stateObject = {};
    if (current !== state.page) {
      _.assign(stateObject, { page: current });
    }
    if (pageSize !== state.pageSize) {
      _.assign(stateObject, { pageSize });
    }
    if (!_.isEmpty(stateObject)) {
      setState(stateObject);
    }
  };

  /**
   * Opens an instant report that already exists for the requested range. Closes the create modal and navigates to that report's inbox route.
   *
   * @param id - Id of the existing instant report.
   * @returns Nothing.
   */
  const handleViewExistedInstantReport = (id: string) => {
    setState({ isOpenCreateInstantReportModal: false });
    navigate(`/studies/inbox/${id}`);
  };

  /**
   * Applies an instant-report socket update to the tab's list. Ignores messages for another study and, when the report exists in a cloned list, overwrites its status and artifact-report flag, otherwise fetches and prepends the report as a new row.
   *
   * @param msg - Socket message with the study friendly id, report id, status and artifact flag.
   * @returns Nothing.
   */
  const handleInstantReportUpdatedSocket = (msg: InstantReportUpdatedSocketMessage) => {
    if (msg.studyFid === data.friendlyId) {
      const clonedReports = _.cloneDeep(state.reports);
      /**
       * The instant report in the cloned report list whose id matches the id carried by the socket message. Resolved with a lodash find over clonedReports, and is undefined when the updated report is not yet present locally.
       *
       * @returns The matching instant report, or undefined when no report in the cloned list has that id.
       */
      const foundReport = _.find(clonedReports, (x) => x.id === msg.id);
      if (foundReport) {
        Object.assign(foundReport, {
          status: msg.status,
          isArtifactReport: msg.isArtifactReport,
        });
        setState({ reports: clonedReports });
      } else {
        handleAddNewInstantReportViaSocket(msg.id);
      }
    }
  };

  useEffect(() => {
    if (isLoadedData && !isC1cProviderStudy) {
      if (isActive && !state.isLoadedData) {
        if (data.start) {
          fetchReports();
        } else {
          setState({
            isLoadedData: true,
            hasNoReport: true,
          });
        }
      }
    }
  }, [isActive, isLoadedData]);

  useUpdateEffect(() => {
    if (state.hideElementInitial) {
      setState({ hideElementInitial: false });
    }
    if (_.isEmpty(state.reports) && state.isLoadedData) {
      setState({ hasNoReport: true });
    } else {
      setState({ hasNoReport: false });
    }
  }, [state.reports]);

  useEmitter(EMITTER_CONSTANTS.INSTANT_REPORT_UPDATED, handleInstantReportUpdatedSocket, [
    state.reports,
  ]);

  const isIcorDevice = checkIcorDevice(data.device);

  return {
    state,
    isAbortedStudy,
    isArchivedStudy,
    isIcorDevice,
    onClickReport,
    handleOpenCreateInstantReportModal,
    handleCancelCreateInstantReportModal,
    handleAfterCreatingInstantReport,
    handleTableChange,
    handleViewExistedInstantReport,
  };
};
